import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  ServiceContext,
} from 'views/EMR/components/ServiceLookup/ServiceContext';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  VirtualTableColumnDef as ColumnDef,
  EditableTableMeta,
} from 'core/components/VirtualTable/types';
import {
  VisitNoteBillingCodeLinkedICDCodeDTO,
} from 'dtos/visitNoteBillingCodeLinkedICDCodeDTO';
import SelectableProvider from 'core/context/selectable';
import {
  useSelectableContext,
} from 'core/hooks';
import ToolTip from 'core/components/Tooltip';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Tag from 'core/components/Tag';
import Button from 'pages/Dashboard/components/Button';
import AddIcon from '@mui/icons-material/Add';
import map from 'lodash/map';
import compact from 'lodash/compact';
import {
  VisitNoteBillingAssessmentCodeDTO,
} from 'dtos/visitNoteBillingAssessmentCodeDTO';
import {
  ReadonlyContext,
  ReadonlyContextProps,
} from 'core/context/readonly';
import isEqual from 'lodash/isEqual';
import {
  CellContext,
} from '@tanstack/react-table';

type Props = {
  assessmentCodes: VisitNoteBillingAssessmentCodeDTO[];
  value: VisitNoteBillingCodeLinkedICDCodeDTO[];
  onChange: (value: VisitNoteBillingCodeLinkedICDCodeDTO[]) => void;
  readonly?: boolean;
};

type AssessmentCodeOptionProps = {
  assessmentCode: VisitNoteBillingAssessmentCodeDTO;
  checked: boolean;
  onToggle: (code: string) => void;
};

function AssessmentCodeOption({
  assessmentCode,
  checked,
  onToggle,
}: AssessmentCodeOptionProps) {
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const code = assessmentCode?.code ?? '';
  const description = assessmentCode?.description ?? '';

  return (
    <div className="h-8 shrink-0 flex items-center min-w-0">
      <Checkbox
        color="secondary"
        id={code}
        checked={checked}
        onChange={() => {
          onToggle(code);
        }}
        classes={{ root: 'min-w-0 w-full', label: 'min-w-0' }}
        label={{
          text: (
            <div className="flex items-center gap-2 min-w-0">
              <Tag
                tagName={code || '--'}
                className="shrink-0"
                defaultColor={clsx('bg-gray-200', {
                  '!bg-blue-200': checked,
                })}
              />
              <ToolTip
                title={description}
                auto
                ref={descriptionRef}
                placement="right"
                arrow={false}
              >
                <span
                  ref={descriptionRef}
                  className="flex-1 min-w-0 truncate text-gray-600"
                >
                  {description}
                </span>
              </ToolTip>
            </div>
          ),
        }}
      />
    </div>
  );
}

function EditableICDCodes({
  value: linkedICDCodes = [],
  onChange,
  assessmentCodes,
  readonly,
}: Props) {
  const { isSelected, toggle, add, clear, selected, remove } = useSelectableContext();

  const selectedLinkedICDCodes = useMemo(() => new Set(compact(map(linkedICDCodes, 'code'))), [linkedICDCodes]);
  const orderedSelectedCodes = useMemo(
    () => assessmentCodes.filter(({ code }) => selected.has(code ?? '')),
    [assessmentCodes, selected],
  );

  const [apply, setApply] = useState<boolean>(false);

  useLayoutEffect(() => {
    const removedCodes = (Array.from(selected) as string[]).filter(
      (code) => !selectedLinkedICDCodes.has(code ?? ''),
    );

    if (removedCodes?.length > 0) {
      remove(...compact(removedCodes));
    }
  }, [linkedICDCodes, selectedLinkedICDCodes]);

  useLayoutEffect(() => {
    if (apply) {
      if (!isEqual(orderedSelectedCodes, linkedICDCodes)) {
        onChange?.(orderedSelectedCodes);
      }
      setApply(false);
    }
  }, [selected]);

  const isAllSelected = useMemo(
    () => selected.size === assessmentCodes?.length,
    [selected, assessmentCodes],
  );
  const hide = assessmentCodes?.length === 0 || readonly;

  return (
    <ToolTip
      hide={hide}
      placement="top-start"
      isInteractive
      delay={300}
      offset={[26, -48]}
      disableFocusListener={false}
      classes={{ tooltip: '!p-0 !max-w-none', arrow: '!hidden' }}
      title={(
        <div className="flex flex-col w-80">
          <Checkbox
            label="All Codes"
            color="secondary"
            indeterminate={selected.size > 0 && !isAllSelected}
            id="all"
            classes={{ root: '!px-2 !py-2' }}
            checked={isAllSelected}
            onChange={() => {
              if (isAllSelected) {
                clear();
              } else {
                const unselected = assessmentCodes.filter(
                  (item) => !isSelected(item?.code ?? ''),
                )?.map((item) => item?.code ?? '');
                add(...unselected);
              }
              setApply(true);
            }}
          />
          <div className="flex flex-col border-t border-gray-300 p-2 max-h-[25rem] overflow-y-auto">
            {assessmentCodes?.map((assessmentCode) => (
              <AssessmentCodeOption
                key={assessmentCode?.code ?? ''}
                assessmentCode={assessmentCode}
                checked={isSelected(assessmentCode?.code ?? '')}
                onToggle={(code) => {
                  toggle(code);
                  setApply(true);
                }}
              />
            ))}
          </div>
        </div>
      )}
    >
      <div className="max-w-full">
        {!hide && selected.size === 0 ? (
          <Button
            className="!font-medium !border-dashed !rounded-full !py-0 !px-1 !w-5 !h-5 !min-w-[initial] !flex !items-center !border-blue-500"
            color="primary"
            type="button"
            variant="outlined"
          >
            <AddIcon
              className="!w-4 !h-4"
              fontSize="small"
            />
          </Button>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {orderedSelectedCodes.map(({ code }) => (
              <Tag key={code} defaultColor="bg-blue-200">
                {code}
              </Tag>
            ))}
          </div>
        )}
        {hide && selected.size === 0 && '--'}
      </div>
    </ToolTip>
  );
}

function LinkedICDCodesCell({ row, table, column }: CellContext<VisitNoteServiceCode, unknown>) {
  const { readonly } = useContext<ReadonlyContextProps>(ReadonlyContext);
  const { getInstanceId, assessmentCodes } = useContext(ServiceContext);
  const { updateData } = table.options.meta as EditableTableMeta<VisitNoteServiceCode>;
  const { linkedICDCodes } = row.original ?? {};
  const instanceId = getInstanceId(row.original);
  const codes = compact(map(linkedICDCodes, 'code'));

  return (
    <SelectableProvider key={instanceId} selected={codes}>
      <EditableICDCodes
        value={linkedICDCodes ?? []}
        assessmentCodes={assessmentCodes ?? []}
        onChange={(newValue) => {
          updateData?.(row.index, column.id, newValue, row?.original);
        }}
        readonly={readonly}
      />
    </SelectableProvider>
  );
}

export default {
  id: 'linkedICDCodes',
  accessorKey: 'linkedICDCodes',
  columnTitle: (
    <>
      Corresponding
      <br />
      Diagnosis
    </>
  ),
  minSize: 100,
  maxSize: 160,
  cell: LinkedICDCodesCell,
} as ColumnDef<VisitNoteServiceCode>;
